import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { url_234_1 } from "../../../custom_utilities/api_services";
import "./Banner.scss";

const AUTO_SCROLL_MS = 3000;

const Banner = () => {
  const [banners, setBanners] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const indexRef = useRef(0);

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const response = await axios.get(url_234_1);
        if (response.data.status === 200 && Array.isArray(response.data.data)) {
          setBanners(response.data.data);
        }
      } catch (error) {
        console.error("Failed to load banners", error);
      }
    };

    fetchBanners();
  }, []);

  useEffect(() => {
    indexRef.current = currentIndex;
  }, [currentIndex]);

  useEffect(() => {
    if (banners.length <= 1 || isHovered) return undefined;

    const timer = setInterval(() => {
      const nextIndex = (indexRef.current + 1) % banners.length;
      indexRef.current = nextIndex;
      setCurrentIndex(nextIndex);
    }, AUTO_SCROLL_MS);

    return () => clearInterval(timer);
  }, [banners.length, isHovered]);

  const goToSlide = (index) => {
    indexRef.current = index;
    setCurrentIndex(index);
  };

  const goToNext = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (banners.length <= 1) return;

    setCurrentIndex((prev) => {
      const nextIndex = (prev + 1) % banners.length;
      indexRef.current = nextIndex;
      return nextIndex;
    });
  };

  const goToPrevious = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (banners.length <= 1) return;

    setCurrentIndex((prev) => {
      const nextIndex = (prev - 1 + banners.length) % banners.length;
      indexRef.current = nextIndex;
      return nextIndex;
    });
  };

  const handleBannerClick = () => {
    const bannerUrl = banners[currentIndex]?.banner_url;
    if (!bannerUrl) return;
    window.open(bannerUrl, "_blank", "noopener,noreferrer");
  };

  const activeBanner = banners[currentIndex];

  if (!activeBanner) {
    return <div className="banner-carousel banner-carousel--loading" />;
  }

  return (
    <div className="banner-carousel">
      <div
        className="banner-carousel__frame"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div
          className="banner-carousel__slide"
          onClick={handleBannerClick}
          role="link"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              handleBannerClick();
            }
          }}
          aria-label="Open banner link"
        >
          <img
            src={activeBanner.banner_image}
            alt="Motion banner"
            className="banner-carousel__image"
            draggable={false}
          />
        </div>

        {banners.length > 1 ? (
          <>
            <button
              type="button"
              className={`banner-carousel__arrow banner-carousel__arrow--prev ${
                isHovered ? "is-visible" : ""
              }`}
              onClick={goToPrevious}
              onPointerDown={(event) => event.stopPropagation()}
              aria-label="Previous banner"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M14.5 6.5L9 12L14.5 17.5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            <button
              type="button"
              className={`banner-carousel__arrow banner-carousel__arrow--next ${
                isHovered ? "is-visible" : ""
              }`}
              onClick={goToNext}
              onPointerDown={(event) => event.stopPropagation()}
              aria-label="Next banner"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M9.5 6.5L15 12L9.5 17.5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </>
        ) : null}
      </div>

      {banners.length > 1 ? (
        <div className="banner-carousel__dots">
          {banners.map((banner, index) => (
            <button
              key={banner.id ?? index}
              type="button"
              className={`banner-carousel__dot ${
                currentIndex === index ? "is-active" : ""
              }`}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                goToSlide(index);
              }}
              aria-label={`Go to banner ${index + 1}`}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default Banner;
